'use client';

import React, { useState, useEffect } from "react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import SearchableMedicineDropdown from "../../components/SearchableMedicineDropdown";
import {
    Plus,
    Trash2,
    Calendar,
    Pill,
    IndianRupee,
    ShoppingCart,
    AlertCircle,
    Save,
    Package,
    Hash,
    CalendarDays,
    Percent,
    Gift,
    Tag
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const NewPurchase = () => {
    const [currentDate] = useState(new Date().toISOString().split("T")[0]);
    const [medicines, setMedicines] = useState([
        {
            id: Date.now(),
            medicine: "",
            batchNumber: "",
            expiryDate: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split("T")[0],
            manufactureDate: new Date().toISOString().split("T")[0],
            quantity: 0,
            free_quantity: 0,
            pricePerPackage: "0",
            mrp: "0",
            pricePerUnit: "0",
            sellingPricePerPackage: "",
            sellingPricePerUnit: "",
            totalPrice: 0,
            totalUnits: 0,
            unitType: "",
            gstPercentage: 0,
            discountPercentage: 0,
            gstType: "CGST_SGST"
        },
    ]);
    const [supplier, setSupplier] = useState("");
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const navigate = useNavigate();

    const [purchaseSummary, setPurchaseSummary] = useState({
        subtotal: 0,
        totalGst: 0,
        totalDiscount: 0,
        grandTotal: 0,
        totalItems: 0,
        totalUnits: 0
    });
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [errors, setErrors] = useState({});

    const [focusedRowId, setFocusedRowId] = useState(null); // New State for focus management

    const paymentStatusOptions = [
        { value: 'pending', label: 'PENDING' },
        { value: 'partial', label: 'PARTIAL' },
        { value: 'paid', label: 'PAID' }
    ];
    const paymentMethodOptions = [
        { value: 'CASH', label: 'Cash' },
        { value: 'CHEQUE', label: 'Cheque' },
        { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
        { value: 'CREDIT', label: 'Credit' },
        { value: 'UPI', label: 'UPI' },
        { value: 'CARD', label: 'Card' }
    ];

    useEffect(() => {
        fetchSuppliers();
    }, []);

    useEffect(() => {
        calculateTotals();
    }, [medicines]);

    const fetchSuppliers = async () => {
        try {
            const response = await axiosInstance.get("/supplier");
            const data = Array.isArray(response.data) ? response.data : [];
            setSuppliers(data);
        } catch (error) {
            console.error("Error fetching suppliers:", error);
            setSuppliers([]);
            showAlertMessage("Failed to load suppliers", "error");
        }
    };

    const calculateTotals = () => {
        let subtotal = 0, totalGst = 0, totalDiscount = 0, totalItems = 0, totalUnits = 0;

        medicines.forEach(med => {
            if (med.medicine && med.quantity > 0) {
                const qty = parseFloat(med.quantity) || 0;
                const pricePkg = parseFloat(med.pricePerPackage) || 0;
                const gstPct = parseFloat(med.gstPercentage) || 0;
                const discPct = parseFloat(med.discountPercentage) || 0;

                const itemSubtotal = qty * pricePkg;
                const itemDiscount = itemSubtotal * (discPct / 100);
                const discounted = itemSubtotal - itemDiscount;
                const itemGst = discounted * (gstPct / 100);

                subtotal += itemSubtotal;
                totalDiscount += itemDiscount;
                totalGst += itemGst;
                totalItems += qty;
                totalUnits += med.totalUnits || 0;
            }
        });

        const grandTotal = subtotal - totalDiscount + totalGst;
        setPurchaseSummary({
            subtotal: Number(subtotal.toFixed(2)),
            totalGst: Number(totalGst.toFixed(2)),
            totalDiscount: Number(totalDiscount.toFixed(2)),
            grandTotal: Number(grandTotal.toFixed(2)),
            totalItems,
            totalUnits
        });
    };

    const getMedicineById = async (medicineId) => {
        try {
            const response = await axiosInstance.get(`/medicine/${medicineId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching medicine:", error);
            return null;
        }
    };

    const handleMedicineChange = async (index, field, value) => {
        const updated = [...medicines];
        updated[index][field] = value;

        if (["quantity", "free_quantity", "pricePerPackage", "mrp", "discountPercentage"].includes(field) && updated[index].medicine) {
            // Try to get units_per_package from API; handle different response shapes safely
            const medData = await getMedicineById(updated[index].medicine);
            const qty = parseFloat(updated[index].quantity) || 0;
            const freeQty = parseFloat(updated[index].free_quantity) || 0;
            const pricePkg = parseFloat(updated[index].pricePerPackage) || 0;
            const mrp = parseFloat(updated[index].mrp) || 0;

            let unitsPerPkg = 1;
            if (medData) {
                unitsPerPkg = medData?.medicine?.units_per_package ?? medData?.units_per_package ?? 1;
            }

            // If unitsPerPkg is missing or invalid, default to 1
            unitsPerPkg = Number(unitsPerPkg) || 1;

            // totalUnits = (quantity + free_quantity) * units_per_package
            updated[index].totalUnits = (qty + freeQty) * unitsPerPkg;

            // price per unit derived from package price
            updated[index].pricePerUnit = unitsPerPkg ? (pricePkg / unitsPerPkg).toFixed(2) : (pricePkg).toFixed(2);

            // total price for the line is quantity * package price
            updated[index].totalPrice = (qty * pricePkg).toFixed(2);

            if (mrp > 0) {
                // Always divide by units_per_package to get per-unit price
                // If unitsPerPkg is 0, null, or undefined, treat as 1 (single unit)
                const effectiveUnitsPerPkg = unitsPerPkg || 1;
                updated[index].sellingPricePerUnit = (mrp / effectiveUnitsPerPkg).toFixed(2);
                updated[index].sellingPricePerPackage = mrp.toFixed(2);
            }
        }
        setMedicines(updated);
    };

    const handleMedicineSelect = async (index, selectedMedicine) => {
        const updated = [...medicines];
        const batchDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');

        if (selectedMedicine) {
            updated[index].medicine = selectedMedicine.id;
            updated[index].unitType = selectedMedicine.uom;
            updated[index].gstPercentage = selectedMedicine.gst_percentage || 0;
            updated[index].gstType = selectedSupplier?.is_interstate ? 'IGST' : 'CGST_SGST';
            updated[index].medicineName = selectedMedicine.name; // Keep name for display
            updated[index].batchNumber = `BATCH${batchDate}${String(index + 1).padStart(2, '0')}`;

            const unitsPerPkg = selectedMedicine.units_per_package || 1;
            const qty = parseFloat(updated[index].quantity) || 0;
            const freeQty = parseFloat(updated[index].free_quantity) || 0;
            const pricePkg = parseFloat(updated[index].pricePerPackage) || 0;

            updated[index].totalUnits = (qty + freeQty) * (Number(unitsPerPkg) || 1);
            updated[index].pricePerUnit = (pricePkg && unitsPerPkg) ? (pricePkg / unitsPerPkg).toFixed(2) : (pricePkg || 0).toString();
            updated[index].totalPrice = (qty * pricePkg).toFixed(2);
            // Fetch Last Purchase Details
            try {
                const lastPurchaseRes = await axiosInstance.get(`/purchases/last-purchase/${selectedMedicine.id}`);
                if (lastPurchaseRes.data?.data) {
                    updated[index].lastPurchaseInfo = lastPurchaseRes.data.data;
                } else {
                    updated[index].lastPurchaseInfo = null;
                }
            } catch (err) {
                console.error("Failed to fetch last purchase details", err);
                updated[index].lastPurchaseInfo = null;
            }
        } else {
            updated[index].medicine = "";
            updated[index].medicineName = "";
            updated[index].unitType = "";
            updated[index].batchNumber = "";
            updated[index].lastPurchaseInfo = null;
        }
        setMedicines(updated);

        // Robust Focus Shift to Batch Number
        if (selectedMedicine) {
            setTimeout(() => {
                const batchInput = document.getElementById(`batch-${index}`);
                if (batchInput) {
                    batchInput.focus();
                    batchInput.select();
                }
            }, 100);
        }
    };

    const handleEnterKey = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const focusable = document.querySelectorAll('input, select, textarea, button, [tabindex="0"]');
            const list = Array.from(focusable).filter(el => !el.disabled && el.tabIndex !== -1);
            const index = list.indexOf(e.target);
            if (index > -1 && index < list.length - 1) {
                list[index + 1].focus();
            }
        }
    };

    const handleLastFieldEnter = (e, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (index === medicines.length - 1) {
                addMedicine();
            } else {
                // Focus next row medicine
                setFocusedRowId(medicines[index + 1].id);
            }
        }
    };

    const addMedicine = () => {
        const newId = Date.now();
        setMedicines(prev => [
            ...prev,
            {
                id: newId,
                medicine: "",
                batchNumber: "",
                expiryDate: new Date(new Date().setMonth(new Date().getMonth() + 6)).toISOString().split("T")[0],
                manufactureDate: new Date().toISOString().split("T")[0],
                quantity: 0,
                free_quantity: 0,
                pricePerPackage: "",
                mrp: "",
                pricePerUnit: "",
                sellingPricePerPackage: "",
                sellingPricePerUnit: "",
                totalPrice: 0,
                totalUnits: 0,
                unitType: "",
                gstPercentage: 0,
                discountPercentage: 0,
                gstType: selectedSupplier?.is_interstate ? 'IGST' : 'CGST_SGST'
            },
        ]);
        setFocusedRowId(newId);
    };

    const removeMedicine = (id) => {
        if (medicines.length > 1) {
            setMedicines(medicines.filter(m => m.id !== id));
        }
    };

    const showAlertMessage = (msg, type) => {
        setAlertMessage(msg);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const validateForm = () => {
        if (!supplier) {
            showAlertMessage("Please select a supplier", "error");
            return false;
        }
        if (!invoiceNumber) {
            showAlertMessage("Please enter invoice number", "error");
            return false;
        }
        if (!paymentStatus) {
            showAlertMessage("Please select payment status", "error");
            return false;
        }
        if (!paymentMethod) {
            showAlertMessage("Please select payment method", "error");
            return false;
        }

        for (let i = 0; i < medicines.length; i++) {
            const m = medicines[i];
            if (!m.medicine) {
                showAlertMessage(`Please select medicine for item ${i + 1}`, "error");
                return false;
            }
            if (!m.batchNumber) {
                showAlertMessage(`Please enter batch number for item ${i + 1}`, "error");
                return false;
            }
            if (!m.expiryDate) {
                showAlertMessage(`Please enter expiry date for item ${i + 1}`, "error");
                return false;
            }
            if (!m.quantity || m.quantity <= 0) {
                showAlertMessage(`Please enter valid quantity for item ${i + 1}`, "error");
                return false;
            }
            if (!m.pricePerPackage || m.pricePerPackage <= 0) {
                showAlertMessage(`Please enter valid price for item ${i + 1}`, "error");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            // Handle payment status mismatch ('paid' vs 'completed')
            const statusToSend = paymentStatus === 'paid' ? 'completed' : paymentStatus;

            const payload = {
                supplier_id: parseInt(supplier),
                purchase_date: currentDate,
                payment_status: statusToSend,
                payment_method: paymentMethod,
                invoice_number: invoiceNumber,
                discount_amount: parseFloat(purchaseSummary.totalDiscount),
                total_amount: parseFloat(purchaseSummary.grandTotal),
                total_gst_amount: parseFloat(purchaseSummary.totalGst),
                items: medicines.map(m => ({
                    medicine_id: parseInt(m.medicine),
                    batch_number: m.batchNumber,
                    expiry_date: m.expiryDate,
                    manufacture_date: m.manufactureDate,
                    quantity: parseInt(m.quantity),
                    free_quantity: parseInt(m.free_quantity) || 0,
                    total_units: parseInt(m.totalUnits),
                    price_per_package: parseFloat(m.pricePerPackage),
                    mrp: parseFloat(m.mrp) || 0,
                    price_per_unit: parseFloat(m.pricePerUnit),
                    selling_price_per_package: parseFloat(m.sellingPricePerPackage) || 0,
                    selling_price_per_unit: parseFloat(m.sellingPricePerUnit) || 0,
                    total_price: parseFloat(m.totalPrice),
                    gst_percentage: parseFloat(m.gstPercentage),
                    gst_type: m.gstType,
                    discount_percentage: parseFloat(m.discountPercentage || 0)
                }))
            };

            await axiosInstance.post('/purchases', payload);
            navigate('/purchase', { state: { successMessage: 'Purchase Created Successfully!' } });
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.error || "Failed to create purchase";
            showAlertMessage(msg, "error");
            setErrors(error.response?.data?.errors || {});
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center space-x-3 mb-2">
                            <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-3 rounded-xl shadow-md">
                                <Plus className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">New Purchase</h1>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">Create a new purchase entry</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Date: <strong>{currentDate}</strong></span>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
                        {/* Supplier & Invoice Section */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 pb-8 border-b border-gray-200">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Supplier <span className="text-red-500">*</span></label>
                                <select
                                    value={supplier}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSupplier(id);
                                        const sup = suppliers.find(s => s.id === parseInt(id));
                                        setSelectedSupplier(sup);
                                        setMedicines(medicines.map(m => ({
                                            ...m,
                                            gstType: sup?.is_interstate ? 'IGST' : 'CGST_SGST'
                                        })));
                                    }}
                                    onKeyDown={handleEnterKey}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                >
                                    <option value="">Select Supplier</option>
                                    {Array.isArray(suppliers) && suppliers.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice # <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={invoiceNumber}
                                    onChange={e => setInvoiceNumber(e.target.value)}
                                    onKeyDown={handleEnterKey}
                                    placeholder="Enter invoice number"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Status <span className="text-red-500">*</span></label>
                                <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)} onKeyDown={handleEnterKey} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all">
                                    {paymentStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method <span className="text-red-500">*</span></label>
                                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} onKeyDown={handleEnterKey} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all">
                                    <option value="">Select Method</option>
                                    {paymentMethodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Medicines Section Header */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    <Package className="w-5 h-5 text-teal-600" />
                                    Items
                                </h2>

                                {/* <button
                                    onClick={addMedicine}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow-md font-medium"
                                >
                                    <Plus size={16} /> Add Item
                                </button> */}
                            </div>
                        </div>

                        <div className="space-y-4 mb-6">
                            {medicines.map((m, i) => (
                                <div key={m.id} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-teal-100 text-teal-700 font-bold w-10 h-10 rounded-full flex items-center justify-center text-lg">
                                                {i + 1}
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-800">
                                                {m.medicineName || "Select Medicine"}
                                            </h3>
                                        </div>
                                        {medicines.length > 1 && (
                                            <button
                                                onClick={() => removeMedicine(m.id)}
                                                className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all hover:scale-105"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Last Purchase Info Alert */}
                                    {m.lastPurchaseInfo && (
                                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3 text-sm text-blue-800 animate-fadeIn">
                                            <div className="bg-blue-100 p-1.5 rounded-full">
                                                <CalendarDays className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <span className="font-semibold">Last Purchased:</span>
                                                <span className="ml-1">
                                                    from <strong>{m.lastPurchaseInfo.supplierName}</strong>
                                                    <span className="mx-1">•</span>
                                                    Price: <strong>₹{m.lastPurchaseInfo.purchasePrice}</strong>
                                                    <span className="mx-1">•</span>
                                                    Date: {new Date(m.lastPurchaseInfo.purchaseDate).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Medicine Selection Row */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <Pill className="w-4 h-4 text-teal-600" />
                                                Medicine Name <span className="text-red-500">*</span>
                                            </label>
                                            <SearchableMedicineDropdown
                                                placeholder="Search and select medicine..."
                                                value={m.medicine}
                                                onChange={med => handleMedicineSelect(i, med)}
                                                onKeyDown={handleEnterKey}
                                                autoFocus={m.id === focusedRowId}
                                                id={`medicine-dropdown-${i}`}
                                            />
                                        </div>
                                        <div className="bg-white p-4 rounded-xl border border-gray-200">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                <Hash className="w-4 h-4 text-teal-600" />
                                                Batch Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id={`batch-${i}`}
                                                value={m.batchNumber}
                                                onChange={e => handleMedicineChange(i, "batchNumber", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                placeholder="Enter batch number"
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Dates Row */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                            <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                                <CalendarDays className="w-4 h-4 text-blue-600" />
                                                Manufacture Date
                                            </label>
                                            <input
                                                type="date"
                                                value={m.manufactureDate}
                                                onChange={e => handleMedicineChange(i, "manufactureDate", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                            />
                                        </div>
                                        <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                                            <label className="block text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                                                <CalendarDays className="w-4 h-4 text-amber-600" />
                                                Expiry Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={m.expiryDate}
                                                onChange={e => handleMedicineChange(i, "expiryDate", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Pricing & Quantity Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                                        <div className="bg-purple-50 p-3 rounded-xl border border-purple-200">
                                            <label className="block text-xs font-semibold text-purple-900 mb-2 flex items-center gap-1">
                                                <ShoppingCart className="w-3 h-3" />
                                                Qty
                                            </label>
                                            <input
                                                type="number"
                                                value={m.quantity}
                                                onChange={e => handleMedicineChange(i, "quantity", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-cyan-50 p-3 rounded-xl border border-cyan-200">
                                            <label className="block text-xs font-semibold text-cyan-900 mb-2 flex items-center gap-1">
                                                <Gift className="w-3 h-3" />
                                                Free Qty
                                            </label>
                                            <input
                                                type="number"
                                                value={m.free_quantity}
                                                onChange={e => handleMedicineChange(i, "free_quantity", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-cyan-300 rounded-lg focus:ring-2 focus:ring-cyan-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-green-50 p-3 rounded-xl border border-green-200">
                                            <label className="block text-xs font-semibold text-green-900 mb-2 flex items-center gap-1">
                                                <IndianRupee className="w-3 h-3" />
                                                Price/Pkg
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.pricePerPackage}
                                                onChange={e => handleMedicineChange(i, "pricePerPackage", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-yellow-50 p-3 rounded-xl border border-yellow-200">
                                            <label className="block text-xs font-semibold text-yellow-900 mb-2 flex items-center gap-1">
                                                <Tag className="w-3 h-3" />
                                                MRP
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.mrp}
                                                onChange={e => handleMedicineChange(i, "mrp", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-orange-50 p-3 rounded-xl border border-orange-200">
                                            <label className="block text-xs font-semibold text-orange-900 mb-2 flex items-center gap-1">
                                                <Percent className="w-3 h-3" />
                                                GST %
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.gstPercentage}
                                                onChange={e => handleMedicineChange(i, "gstPercentage", e.target.value)}
                                                onKeyDown={handleEnterKey}
                                                className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-pink-50 p-3 rounded-xl border border-pink-200">
                                            <label className="block text-xs font-semibold text-pink-900 mb-2 flex items-center gap-1">
                                                <Percent className="w-3 h-3" />
                                                Disc %
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={m.discountPercentage}
                                                onChange={e => handleMedicineChange(i, "discountPercentage", e.target.value)}
                                                onKeyDown={(e) => handleLastFieldEnter(e, i)}
                                                className="w-full px-3 py-2 border border-pink-300 rounded-lg focus:ring-2 focus:ring-pink-500 text-center font-semibold bg-white"
                                            />
                                        </div>

                                        <div className="bg-indigo-50 p-3 rounded-xl border border-indigo-200">
                                            <label className="block text-xs font-semibold text-indigo-900 mb-2">Units</label>
                                            <div className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-center font-bold text-indigo-700 bg-white">
                                                {m.totalUnits}
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-3 rounded-xl border-2 border-teal-300">
                                            <label className="block text-xs font-semibold text-teal-900 mb-2 flex items-center gap-1">
                                                <IndianRupee className="w-3 h-3" />
                                                Total
                                            </label>
                                            <div className="w-full px-3 py-2 border-2 border-teal-400 rounded-lg text-center font-bold text-teal-700 bg-white text-lg">
                                                ₹{parseFloat(m.totalPrice || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Add Item Button at Bottom Right */}
                        <div className="flex justify-end mb-6">
                            <button
                                onClick={addMedicine}
                                className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-md hover:shadow-lg font-medium"
                            >
                                <Plus size={20} /> Add Item
                            </button>
                        </div>

                        {/* Summary Section */}
                        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl shadow-lg p-6 text-white mb-6">
                            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Subtotal</p>
                                    <p className="text-lg font-bold">₹{purchaseSummary.subtotal.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Discount</p>
                                    <p className="text-lg font-bold">₹{purchaseSummary.totalDiscount.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">GST</p>
                                    <p className="text-lg font-bold">₹{purchaseSummary.totalGst.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-20 rounded-lg border border-white border-opacity-30">
                                    <p className="text-xs font-semibold opacity-90 mb-1">Grand Total</p>
                                    <p className="text-xl font-bold">₹{purchaseSummary.grandTotal.toFixed(2)}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Items</p>
                                    <p className="text-lg font-bold">{purchaseSummary.totalItems}</p>
                                </div>
                                <div className="text-center p-3 bg-white bg-opacity-10 rounded-lg">
                                    <p className="text-xs font-semibold opacity-80 mb-1">Units</p>
                                    <p className="text-lg font-bold">{purchaseSummary.totalUnits}</p>
                                </div>
                            </div>
                        </div>

                        {/* Alert */}
                        {showAlert && (
                            <div className={`mb-6 p-4 rounded-xl border ${alertType === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"} flex items-center gap-2`}>
                                <AlertCircle size={20} />
                                <span className="font-medium">{alertMessage}</span>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => navigate(-1)} className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-all font-medium shadow-sm hover:shadow-md">
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-xl hover:shadow-lg disabled:opacity-50 transition-all font-medium shadow-md"
                            >
                                <Save size={18} />
                                {isSubmitting ? "Saving..." : "Save Purchase"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default NewPurchase;